import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from './useAuth'
import companyTranslations from '../modules/i18n/locales/en-company.json'
import { TOptions } from 'i18next'

// Type for the translation object structure
type PartnerTranslationObject = typeof companyTranslations

// Type for all possible translation keys
export type PartnerTranslationKey = keyof PartnerTranslationObject

/**
 * Type-safe translation hook for partner-specific terminology.
 * Uses organizationType as the namespace (e.g. company, school), defaulting to "company".
 *
 * @example
 * ```tsx
 * const { tP } = usePartnerTar();
 *
 * // Type-safe usage
 * const newText = tP('newCohort'); // ✅ Valid - returns "New Position" or "New Class"
 * const invalidText = tP('invalidKey'); // ❌ TypeScript error
 * ```
 */
export function usePartnerTar() {
  const { organizationType } = useAuth()
  const namespace = organizationType ?? 'company'

  const { t: i18nT, i18n } = useTranslation(namespace)

  /**
   * Type-safe translation function.
   * Passes ns and lng explicitly so the correct namespace and language are used
   * after changeLanguage (avoids react-i18next using wrong namespace on language switch).
   */
  const tP = useCallback(
    <K extends PartnerTranslationKey>(key: K, options?: TOptions): string => {
      return i18nT(key, {
        ...options,
        ns: namespace,
        lng: i18n.language,
      }) as PartnerTranslationObject[K]
    },
    [i18nT, i18n.language, namespace]
  )

  return {
    tP,
  }
}
